"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useState, useRef, useMemo, useLayoutEffect, useEffect } from "react";
import { useMotionValue, useSpring, useTransform } from "motion/react";
import { cva, type VariantProps } from "class-variance-authority";
import type { Menu as MenuType, ThemeVariant } from "@/types";
import { MenuItem } from "./MenuItem";
import { MobileSubMenu } from "./mobile/MobileSubMenu";
import { Icon } from "@/components/ui/Icon";
import NextLink from "next/link";
import { usePathname } from "next/navigation";
import { motion, AnimatePresence } from "motion/react";
import { SubMenuSection } from "./SubMenuSection";
import { BurgerButton } from "./mobile/BurgerButton";
const menuVariants = cva("rounded-l-full rounded-r-full flex justify-center h-10", {
  variants: {
    variant: {
      dark: "bg-purple-medium/80 backdrop-blur-2xl",
      light: "bg-black/40 backdrop-blur-2xl"
    }
  },
  defaultVariants: {
    variant: "dark"
  }
});
const mobileMenuVariants = cva("flex flex-col col-span-12 p-sm rounded-3xl", {
  variants: {
    variant: {
      dark: "bg-purple-medium/80",
      light: "bg-black/40"
    }
  },
  defaultVariants: {
    variant: "dark"
  }
});
const pillVariants: Record<string, string> = {
  dark: "bg-purple-dark",
  light: "bg-black"
};
export interface MenuProps extends VariantProps<typeof menuVariants> {
  /** Menu data containing all menu items */
  menu: MenuType;
  /** Optional className for styling */
  className?: string;
}

/**
 * Main navigation menu component.
 * Desktop: renders a single animated "magic pill" that slides to the hovered
 * item and falls back to the currently active item when nothing is hovered.
 * Mobile: uses a conventional slide-in drawer with per-item highlight.
 */
export const Menu: React.FC<MenuProps> = t0 => {
  const $ = _c(110);
  const {
    menu,
    variant,
    className
  } = t0;
  const pathname = usePathname();
  const [isOpen, setIsOpen] = useState(false);
  const [openSubmenuIndex, setOpenSubmenuIndex] = useState(null);
  let t1;
  if ($[0] !== isOpen) {
    t1 = () => {
      const next = !isOpen;
      setIsOpen(next);
      if (!next) {
        setOpenSubmenuIndex(null);
      }
    };
    $[0] = isOpen;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleToggleMenu = t1;
  let t2;
  let t3;
  if ($[2] !== isOpen) {
    t2 = () => {
      window.dispatchEvent(new CustomEvent("mobile-menu-state", {
        detail: {
          isOpen
        }
      }));
      document.body.style.overflow = isOpen ? "hidden" : "";
      return _temp;
    };
    t3 = [isOpen];
    $[2] = isOpen;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const ulRef = useRef(null);
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [];
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const itemRefs = useRef(t4);
  const leftMV = useMotionValue(0);
  const widthMV = useMotionValue(0);
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      stiffness: 500,
      damping: 38
    };
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const pillLeft = useSpring(leftMV, t5);
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      stiffness: 500,
      damping: 38
    };
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const pillWidth = useSpring(widthMV, t6);
  const isFirstPositionSet = useRef(false);
  const submenuWidthMV = useMotionValue(0);
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      stiffness: 500,
      damping: 38
    };
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const submenuWidth = useSpring(submenuWidthMV, t7);
  const isFirstSubmenuWidthSet = useRef(false);
  const submenuContentRef = useRef(null);
  const submenuOffsetMV = useMotionValue(0);
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      stiffness: 500,
      damping: 38
    };
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const submenuOffset = useSpring(submenuOffsetMV, t8);
  const isFirstSubmenuOffsetSet = useRef(false);
  let t10;
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = [-20, 0];
    t10 = [20, 0];
    $[10] = t10;
    $[11] = t9;
  } else {
    t10 = $[10];
    t9 = $[11];
  }
  const submenuTopLeftRadius = useTransform(submenuOffset, t9, t10);
  const submenuBorderRadius = useTransform(submenuTopLeftRadius, _temp2);
  const submenuHeightMV = useMotionValue(0);
  let t11;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      stiffness: 500,
      damping: 38
    };
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  const submenuHeight = useSpring(submenuHeightMV, t11);
  const isFirstSubmenuHeightSet = useRef(false);
  let t12;
  bb0: {
    if (pathname === "/" || pathname === "/de") {
      t12 = 0;
      break bb0;
    }
    let t13;
    if ($[13] !== pathname) {
      t13 = item => item.type === "link" ? pathname.startsWith(item.data.href) : !!item.data.href && pathname === item.data.href || item.data.items.some(subItem => subItem.links.some(link => pathname === link.href));
      $[13] = pathname;
      $[14] = t13;
    } else {
      t13 = $[14];
    }
    const idx = menu.items.findIndex(t13);
    t12 = idx === -1 ? -1 : idx + 1;
  }
  const activeIndex = t12;
  const targetIndex = hoveredIndex ?? activeIndex;
  let t13;
  if ($[15] !== leftMV || $[16] !== pillLeft || $[17] !== pillWidth || $[18] !== targetIndex || $[19] !== widthMV) {
    t13 = () => {
      if (targetIndex < 0) {
        return;
      }
      const el = itemRefs.current[targetIndex];
      if (!el) {
        return;
      }
      const left = el.offsetLeft;
      const width = el.offsetWidth;
      if (!isFirstPositionSet.current) {
        pillLeft.jump(left);
        pillWidth.jump(width);
        isFirstPositionSet.current = true;
      } else {
        leftMV.set(left);
        widthMV.set(width);
      }
    };
    $[15] = leftMV;
    $[16] = pillLeft;
    $[17] = pillWidth;
    $[18] = targetIndex;
    $[19] = widthMV;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== leftMV || $[22] !== menu.items || $[23] !== pillLeft || $[24] !== pillWidth || $[25] !== targetIndex || $[26] !== widthMV) {
    t14 = [menu.items, targetIndex, leftMV, widthMV, pillLeft, pillWidth];
    $[21] = leftMV;
    $[22] = menu.items;
    $[23] = pillLeft;
    $[24] = pillWidth;
    $[25] = targetIndex;
    $[26] = widthMV;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  useLayoutEffect(t13, t14);
  const pillColorClass = pillVariants[variant ?? "dark"] ?? pillVariants.dark;
  const isLogoActive = pathname === "/" || pathname === "/de";
  const activeItem = menu.items[targetIndex - 1];
  const isSubmenuOpen = hoveredIndex !== null && activeItem?.type === "submenu";
  let t15;
  if ($[28] !== isSubmenuOpen || $[29] !== submenuHeight || $[30] !== submenuHeightMV || $[31] !== submenuOffset || $[32] !== submenuOffsetMV || $[33] !== submenuWidth || $[34] !== submenuWidthMV || $[35] !== targetIndex) {
    t15 = () => {
      if (!isSubmenuOpen) {
        isFirstSubmenuWidthSet.current = false;
        isFirstSubmenuOffsetSet.current = false;
        isFirstSubmenuHeightSet.current = false;
        submenuHeightMV.set(0);
        return;
      }
      if (!submenuContentRef.current) {
        submenuHeightMV.set(0);
        return;
      }
      const width_0 = submenuContentRef.current.offsetWidth;
      const height = submenuContentRef.current.offsetHeight;
      const ulLeft = ulRef.current?.getBoundingClientRect().left ?? 0;
      const itemOffsetLeft = itemRefs.current[targetIndex]?.offsetLeft ?? 0;
      const overflow = ulLeft + itemOffsetLeft + width_0 + 16 - window.innerWidth;
      const offset = overflow > 0 ? -overflow : 0;
      if (!isFirstSubmenuWidthSet.current) {
        submenuWidth.jump(width_0);
        submenuWidthMV.jump(width_0);
        submenuOffset.jump(offset);
        submenuOffsetMV.jump(offset);
        isFirstSubmenuWidthSet.current = true;
        isFirstSubmenuOffsetSet.current = true;
      } else {
        submenuWidthMV.set(width_0);
        submenuOffsetMV.set(offset);
      }
      if (!isFirstSubmenuHeightSet.current) {
        submenuHeight.jump(0);
        submenuHeightMV.set(height);
        isFirstSubmenuHeightSet.current = true;
      } else {
        submenuHeightMV.set(height);
      }
    };
    $[28] = isSubmenuOpen;
    $[29] = submenuHeight;
    $[30] = submenuHeightMV;
    $[31] = submenuOffset;
    $[32] = submenuOffsetMV;
    $[33] = submenuWidth;
    $[34] = submenuWidthMV;
    $[35] = targetIndex;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== activeItem || $[38] !== isSubmenuOpen || $[39] !== submenuHeight || $[40] !== submenuHeightMV || $[41] !== submenuOffset || $[42] !== submenuOffsetMV || $[43] !== submenuWidth || $[44] !== submenuWidthMV || $[45] !== targetIndex) {
    t16 = [activeItem, isSubmenuOpen, targetIndex, submenuWidthMV, submenuWidth, submenuOffsetMV, submenuOffset, submenuHeightMV, submenuHeight];
    $[37] = activeItem;
    $[38] = isSubmenuOpen;
    $[39] = submenuHeight;
    $[40] = submenuHeightMV;
    $[41] = submenuOffset;
    $[42] = submenuOffsetMV;
    $[43] = submenuWidth;
    $[44] = submenuWidthMV;
    $[45] = targetIndex;
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  useLayoutEffect(t15, t16);
  const t17 = `relative container flex items-center justify-between ${className}`;
  let t18;
  if ($[47] !== variant) {
    t18 = menuVariants({
      variant
    });
    $[47] = variant;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  const t19 = `hidden lg:flex ${t18}`;
  let t20;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = () => setHoveredIndex(null);
    $[49] = t20;
  } else {
    t20 = $[49];
  }
  let t21;
  if ($[50] !== activeItem || $[51] !== isSubmenuOpen || $[52] !== pillColorClass || $[53] !== pillLeft || $[54] !== pillWidth || $[55] !== submenuBorderRadius || $[56] !== submenuHeight || $[57] !== submenuOffset || $[58] !== submenuWidth || $[59] !== targetIndex) {
    t21 = targetIndex >= 0 && <motion.div aria-hidden={true} className="absolute top-0 pointer-events-none" style={{
      left: pillLeft
    }}><motion.div className={`h-10 ${pillColorClass} rounded-lg`} style={{
        width: pillWidth
      }} animate={{
        borderRadius: isSubmenuOpen ? "var(--radius-lg) var(--radius-lg) 0px 0px" : "var(--radius-lg) var(--radius-lg) var(--radius-lg) var(--radius-lg)"
      }} transition={{
        type: "spring",
        stiffness: 500,
        damping: 38,
        delay: isSubmenuOpen ? 0 : 0.2
      }} /><motion.div style={{
        width: submenuWidth,
        height: submenuHeight,
        x: submenuOffset,
        borderRadius: submenuBorderRadius
      }} className={`pointer-events-auto overflow-hidden ${pillColorClass}`}><div ref={submenuContentRef} className="p-md flex flex-row gap-md justify-start items-stretch w-max">{activeItem?.type === "submenu" && activeItem.data.items?.map((section, i) => <SubMenuSection key={i} item={section} isResponsiveWidth={activeItem.data.items.length <= 1} />)}</div></motion.div></motion.div>;
    $[50] = activeItem;
    $[51] = isSubmenuOpen;
    $[52] = pillColorClass;
    $[53] = pillLeft;
    $[54] = pillWidth;
    $[55] = submenuBorderRadius;
    $[56] = submenuHeight;
    $[57] = submenuOffset;
    $[58] = submenuWidth;
    $[59] = targetIndex;
    $[60] = t21;
  } else {
    t21 = $[60];
  }
  let t22;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = el_0 => {
      itemRefs.current[0] = el_0;
    };
    $[61] = t22;
  } else {
    t22 = $[61];
  }
  let t23;
  if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = {
      type: "link",
      data: {
        href: "/",
        label: "MUUUH! Logo"
      }
    };
    $[62] = t23;
  } else {
    t23 = $[62];
  }
  let t24;
  let t25;
  let t26;
  if ($[63] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = () => setHoveredIndex(0);
    t25 = () => setHoveredIndex(0);
    t26 = <Icon name="muuuh-logo" className="w-full" />;
    $[63] = t24;
    $[64] = t25;
    $[65] = t26;
  } else {
    t24 = $[63];
    t25 = $[64];
    t26 = $[65];
  }
  let t27;
  if ($[66] !== isLogoActive || $[67] !== variant) {
    t27 = <MenuItem ref={t22} item={t23} variant={variant} type="logo" isActive={isLogoActive} onMouseEnter={t24} onFocus={t25}>{t26}</MenuItem>;
    $[66] = isLogoActive;
    $[67] = variant;
    $[68] = t27;
  } else {
    t27 = $[68];
  }
  let t28;
  if ($[69] !== menu.items || $[70] !== pathname || $[71] !== variant) {
    t28 = menu.items.map((item_0, index) => {
      const isActive = item_0.type === "link" ? pathname.startsWith(item_0.data.href) : !!item_0.data.href && pathname === item_0.data.href || item_0.data.items.some(subItem_0 => subItem_0.links.some(link_0 => pathname === link_0.href));
      return <MenuItem key={index} ref={el_1 => {
        itemRefs.current[index + 1] = el_1;
      }} item={item_0} variant={variant} isActive={isActive} onMouseEnter={() => setHoveredIndex(index + 1)} onFocus={() => setHoveredIndex(index + 1)} />;
    });
    $[69] = menu.items;
    $[70] = pathname;
    $[71] = variant;
    $[72] = t28;
  } else {
    t28 = $[72];
  }
  let t29;
  if ($[73] !== t21 || $[74] !== t27 || $[75] !== t28) {
    t29 = <ul ref={ulRef} className="flex relative" onMouseLeave={t20}>{t21}{t27}{t28}</ul>;
    $[73] = t21;
    $[74] = t27;
    $[75] = t28;
    $[76] = t29;
  } else {
    t29 = $[76];
  }
  let t30;
  if ($[77] !== t19 || $[78] !== t29) {
    t30 = <nav className={t19}>{t29}</nav>;
    $[77] = t19;
    $[78] = t29;
    $[79] = t30;
  } else {
    t30 = $[79];
  }
  let t31;
  if ($[80] !== variant) {
    t31 = menuVariants({
      variant
    });
    $[80] = variant;
    $[81] = t31;
  } else {
    t31 = $[81];
  }
  const t32 = `text-white items-center px-sm md:px-md w-32 outline-offset-0 focus-visible:outline-2 focus-visible:outline-offset-2 ${variant === "light" ? "outline-yellow" : "outline-purple"} ${t31}`;
  let t33;
  if ($[82] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <Icon name="muuuh-logo" className="w-full" />;
    $[82] = t33;
  } else {
    t33 = $[82];
  }
  let t34;
  if ($[83] !== t32) {
    t34 = <NextLink href="/" aria-label="MUUUH! Startseite" className={t32}>{t33}</NextLink>;
    $[83] = t32;
    $[84] = t34;
  } else {
    t34 = $[84];
  }
  const t35 = variant as ThemeVariant;
  let t36;
  if ($[85] !== handleToggleMenu || $[86] !== isOpen || $[87] !== t35) {
    t36 = <BurgerButton variant={t35} isMenuOpen={isOpen} onToggle={handleToggleMenu} />;
    $[85] = handleToggleMenu;
    $[86] = isOpen;
    $[87] = t35;
    $[88] = t36;
  } else {
    t36 = $[88];
  }
  let t37;
  if ($[89] !== t34 || $[90] !== t36) {
    t37 = <div className="relative z-50 flex w-full items-center justify-between lg:hidden">{t34}{t36}</div>;
    $[89] = t34;
    $[90] = t36;
    $[91] = t37;
  } else {
    t37 = $[91];
  }
  let t38;
  if ($[92] !== isOpen) {
    t38 = isOpen && <motion.div key="mobile-blur-overlay" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease: "linear"
    }} className="fixed inset-0 z-30 backdrop-blur-xl lg:hidden" />;
    $[92] = isOpen;
    $[93] = t38;
  } else {
    t38 = $[93];
  }
  let t39;
  if ($[94] !== t38) {
    t39 = <AnimatePresence>{t38}</AnimatePresence>;
    $[94] = t38;
    $[95] = t39;
  } else {
    t39 = $[95];
  }
  let t40;
  if ($[96] !== isOpen || $[97] !== menu.items || $[98] !== openSubmenuIndex || $[99] !== pathname || $[100] !== variant) {
    t40 = isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease: "linear"
    }} className="absolute top-full mt-2 left-md right-md md:left-lg md:right-lg z-40 lg:hidden overflow-y-auto max-h-[calc(100svh-4rem)] rounded-3xl backdrop-blur-2xl"><nav className={mobileMenuVariants({
        variant
      })}><ul className="flex flex-col space-y-sm">{menu.items.map((item_1, index_0) => <MobileSubMenu key={index_0} data={item_1.data} items={item_1.type === "submenu" ? item_1.data.items : []} variant={variant} isOpen={openSubmenuIndex === index_0} onToggle={() => setOpenSubmenuIndex(openSubmenuIndex === index_0 ? null : index_0)} isActive={!!item_1.data.href && pathname === item_1.data.href || item_1.type === "submenu" && item_1.data.items.some(subItem_1 => subItem_1.links.some(link_1 => pathname === link_1.href))} />)}</ul></nav></motion.div>;
    $[96] = isOpen;
    $[97] = menu.items;
    $[98] = openSubmenuIndex;
    $[99] = pathname;
    $[100] = variant;
    $[101] = t40;
  } else {
    t40 = $[101];
  }
  let t41;
  if ($[102] !== t40) {
    t41 = <AnimatePresence>{t40}</AnimatePresence>;
    $[102] = t40;
    $[103] = t41;
  } else {
    t41 = $[103];
  }
  let t42;
  if ($[104] !== t17 || $[105] !== t30 || $[106] !== t37 || $[107] !== t39 || $[108] !== t41) {
    t42 = <div className={t17}>{t30}{t37}{t39}{t41}</div>;
    $[104] = t17;
    $[105] = t30;
    $[106] = t37;
    $[107] = t39;
    $[108] = t41;
    $[109] = t42;
  } else {
    t42 = $[109];
  }
  return t42;
};
function _temp() {
  document.body.style.overflow = "";
}
function _temp2(v) {
  return `${v}px var(--radius-lg) var(--radius-lg) var(--radius-lg)`;
}